import React, { useEffect, useState } from "react";

import { Trans, useTranslation } from "react-i18next";

import { SuccessAlert } from "../../utils/Alerts";
import MeasurementsAPI from "../../API/measurements.api";
import { handleReadingFile } from "../../utils/FileHandler";
import LogCapture from "../../utils/LogManagement/LogHelper";
import BaseModal from "./BaseModal";

const LocalUploadModal = ({
    modalButton,
    modalButtonSetter,
    data,
    settings,
    setSettings,
    selectedMaterial,
    setData,
    setDataFileName,
    setMeasurementsProgressBarValue,
    resetReferences,
    setMaxAllowedKFold,
    isTesting,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [options, setOptions] = useState({});

    const [file, setFile] = useState(null);
    const [isFileSubmitted, setIsFileSubmitted] = useState(false);

    const [fileData, setFileData] = useState(null);
    const [filename, setFilename] = useState(null);
    const [allowedKFold, setAllowedKFold] = useState(null);

    const { t } = useTranslation();

    useEffect(() => {
        if (modalButton) {
            openModal();
            modalButtonSetter(false);
        }
    }, [modalButton]);

    useEffect(() => {
        if (fileData && fileData.rows && fileData.rows[0]) {
            setOptions((prevOptions) => ({
                ...prevOptions,
                interpolation: `${fileData?.rows[0]?.length - 1} points`,
            }));
        }
    }, [fileData]);

    useEffect(() => {
        setOptions({
            interpolation: "257 points",
            apodization: settings?.apodization || "Boxcar",
            FFTPoints: settings?.FFTPoints || "32k points",
            gain: settings?.gain || "Default (0)",
            scanTime: settings?.scanTime || "5000",
        });
    }, [settings]);

    useEffect(() => {
        if (data && isFileSubmitted) {
            handleSubmit();
        }
    }, [data, isFileSubmitted]);

    const openModal = () => {
        setIsVisible(true);
    };

    const handleFileRead = (event) => {
        handleReadingFile(
            event,
            () => {
                ErrorAlert(t("readingFileError"));
                LogCapture.error("Unable to read local measurements file");
            },
            (key, retrievedFilename, result) => {
                const distinct = [...new Set(result.rows.map((row) => row[0]))];
                if (!isTesting) {
                    setAllowedKFold(distinct.length);
                }
                setFileData(result);
                setFilename(retrievedFilename);
                LogCapture.info(
                    `User uploaded local measurements file, filename: ${retrievedFilename}`
                );
            }
        );
    };

    const returnCurrentMetadata = (givenMetadata, index) => {
        let returnedMetadata = {};
        if (givenMetadata) {
            if (givenMetadata.createdAt)
                returnedMetadata.createdAt = givenMetadata.createdAt[index];
            if (givenMetadata.scannerId)
                returnedMetadata.scannerId = givenMetadata.scannerId[index];
            if (givenMetadata.createdBy)
                returnedMetadata.createdBy = givenMetadata.createdBy[index];
        }
        return returnedMetadata;
    };
    const handleSubmit = async () => {
        setIsVisible(false);
        setIsSubmitting(false);
        setIsFileSubmitted(false);
        SuccessAlert(t("dataImportedSuccessMessage"));
        setSettings({ ...options });
        let ids = [];
        let index,
            len,
            temporaryRows,
            chunk = 10;

        for (index = 0, len = data.rows.length; index < len; index += chunk) {
            temporaryRows = data.rows.slice(index, index + chunk);

            const reqRowData = temporaryRows.map((row) => {
                const metadata = returnCurrentMetadata(data.metadata, index);

                return {
                    type: "Spectrum",
                    materialId: selectedMaterial,
                    name: row[0],
                    numberOfPoints: row.length - 1,
                    spectralData: row.slice(1),
                    ...metadata,
                };
            });

            const wavenumber = data.headers;

            const req = {
                data: reqRowData,
                wavenumber,
                imported: true,
                settings: { ...options },
            };

            const res = await MeasurementsAPI.syncLocal(req);
            ids.push(...res.map((id) => ({ id })));
            setMeasurementsProgressBarValue(
                Math.ceil((index / data.rows.length) * 100)
            );
            LogCapture.info(`Bulk measurements created with ids: ${ids}`);
        }

        setMeasurementsProgressBarValue(100);
        setData({ ...data, ids });
    };

    const handleSave = () => {
        if (!isTesting) {
            setMaxAllowedKFold(allowedKFold);
        }
        setIsFileSubmitted(true);
        setData(fileData);
        setDataFileName(filename);
        resetReferences();
    };

    const handleFileClick = (id) => {
        const element = document.getElementById(id);
        element.click();
        element.value = "";
    };

    const handleFileChange = (event) => {
        if (event.target.files.length === 0) return;
        const selectedFile = event.target.files[0];

        setFile(selectedFile);
        handleFileRead(event);
    };

    const handleInputChange = (event) => {
        setOptions((prevOptions) => ({
            ...prevOptions,
            [event.target.name]: event.target.value,
        }));
    };

    const fields = [
        {
            label: (
                <label htmlFor="settings-input-1" className="uppercase">
                    <Trans i18nKey="Interpolation">Interpolation</Trans>
                </label>
            ),
            input: (
                <input
                    onChange={handleInputChange}
                    value={options.interpolation || ""}
                    name="interpolation"
                    type="text"
                    id="settings-input-1"
                    className="input-element"
                />
            ),
        },
        {
            label: (
                <label htmlFor="settings-input-2" className="uppercase">
                    <Trans i18nKey="apodization">Apodization</Trans>
                </label>
            ),
            input: (
                <input
                    name="apodization"
                    value={options.apodization || ""}
                    onChange={handleInputChange}
                    id="settings-input-2"
                    className="input-element"
                />
            ),
        },
        {
            label: (
                <label htmlFor="settings-input-3" className="uppercase">
                    <Trans i18nKey="fftPoints">FFTPoints</Trans>
                </label>
            ),
            input: (
                <input
                    onChange={handleInputChange}
                    value={options.FFTPoints || ""}
                    name="FFTPoints"
                    type="text"
                    id="settings-input-3"
                    className="input-element"
                />
            ),
        },
        {
            label: (
                <label htmlFor="settings-input-4" className="uppercase">
                    <Trans i18nKey="gain">Gain</Trans>
                </label>
            ),
            input: (
                <input
                    name="gain"
                    value={options.gain || ""}
                    onChange={handleInputChange}
                    id="settings-input-4"
                    className="input-element"
                />
            ),
        },
        {
            label: (
                <label htmlFor="settings-input-5" className="uppercase">
                    <Trans i18nKey="scanTime">Scan Time</Trans>
                </label>
            ),
            input: (
                <input
                    onChange={handleInputChange}
                    value={options.scanTime || 0}
                    name="scanTime"
                    type="text"
                    id="settings-input-5"
                    className="input-element"
                />
            ),
        },
    ];

    const renderImportMeasurements = () => {
        return (
            <>
                <button
                    onClick={() => handleFileClick("openFile-1")}
                    className="secondary-green-btn mr-2"
                    id="import-measurements-modal-btn"
                >
                    <Trans i18nKey="importMeasurements">
                        Import Measurements
                    </Trans>
                    <input
                        id="openFile-1"
                        type="file"
                        name="data"
                        accept=".csv"
                        className="hidden"
                        onChange={handleFileChange}
                    />
                </button>

                {file && (
                    <>
                        <label className="uppercase text-light-blue mr-2">
                            <Trans i18nKey="filename">filename:</Trans>
                        </label>
                        <span className="text-xs text-primary-blue">
                            {filename}
                        </span>
                    </>
                )}
            </>
        );
    };

    const renderFields = () => {
        return (
            fields &&
            fields.map((field, index) => {
                return (
                    <div className="input-group" key={index}>
                        {field.label}
                        {field.input}
                    </div>
                );
            })
        );
    };

    return (
        <>
            <BaseModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                isSubmitting={isSubmitting}
                titleIcon="fas fa-upload"
                title={t("importMeasurements")}
                action={{
                    name: t("save"),
                    disabled: !file,
                    submitAction: handleSave,
                }}
                isSmall
            >
                <div className="main-customer-form-inputs w-full grid-3-cols pb-5">
                    {renderFields()}
                </div>

                <div className="flex items-center">
                    {renderImportMeasurements()}
                </div>
            </BaseModal>
        </>
    );
};

export default LocalUploadModal;
